import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Chevron from '../Theme/Icons/Chevron';
import '../Theme/Styles/Accordion.css';
import styles from '../Theme/Styles/styles.module.css';
import themes from '../Theme/Themes';
import localization from '../Language/localization';
import { SearchAutocomplete, Item } from '@react-spectrum/autocomplete';
import { Checkbox } from '@adobe/react-spectrum';
import Vip from '../Theme/Icons/Vip';
import { CartDetails } from './CartDetails';
import axios from 'axios';
import { updateCustomerDetails, updateSearchCustomerDetails } from '../Store/actions/customerInfoActions';
import isEqual from 'lodash/isEqual';
import { useDebounce } from 'use-debounce';
import getAuthHeaders from '../helpers/apiHelpers';
import LinkedActionButton from '../Theme/Icons/linkActionButton';

declare const window: any;
window.value_add_action = {};

interface Props {
  key: string;
  title: string;
  callBackEvent: (eventType: string, eventData: any) => void;
  setReload: (reload: any) => void;
  feedback?: any;
  agentPath: Array<{ DisplayName: string; ContactId: string; ParticipantId: string }> | string;
  cipValues: any;
  isCIP: boolean;
  reload: any;
  chatStatus: any;
  callOrigin: string;
  transferInProgressId: string;
  conversationSummary: any;
  conversationSentiments: {
    [contactId: string]: string;
  };
  conversationSentimentsLoading: {
    [contactId: string]: boolean;
  };
  sentimentEnabled: boolean;
  trendLineData: any;
  currentConvId: string;
  initialContactId: string;
  urls: {
    HENDRIX_DOMAIN: string;
    CM_DOMAIN: string;
    CIP_DOMAIN: string;
    CCP_API_END_POINT: string;
    RTS_PROCESSOR_ENDPOINT: string;
  };
}
interface Employee {
  name: string;
  ldap: string;
  isDirectorOrAbove?: string;
}

function Accordion({
  title,
  cipValues,
  isCIP,
  urls,// @ts-ignore
  reload, // @ts-ignore
  callBackEvent,
  currentConvId,
  initialContactId
}: Props) {
  const content: any = useRef('0px');
  const [active, setActive] = useState(true);
  const [linked, updateCustomerLink] = useState(false);
  const [height, setHeight] = useState('0px');
  const [rotate, setRotate] = useState('accordion-icon rotate');
  const [inputHistory, setInputHistory] = useState<{ [key: string]: string }>(() => {
    const savedHistory = sessionStorage.getItem('inputHistory');
    return savedHistory ? JSON.parse(savedHistory) : {};
  });
  const [agentInput, setAgentInput] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();

  const isConversationActive: boolean = useSelector((state: any) => state.customerInfo.isConversationActive);
  const { userPersisted, searchedUser } = useSelector((state: any) => state.customerInfo);
  useEffect(() => {
    if (isConversationActive) setAgentInput(inputHistory[currentConvId] || '');
  }, [inputHistory]);

  useEffect(() => {
    sessionStorage.setItem('inputHistory', JSON.stringify(inputHistory));
  }, [inputHistory]);



  const searchEmployee = async (input: string, autolink: boolean = true, updateFields: boolean = false, getSingleMatch: boolean = false) => {
    setError('');
    setLoading(true);
    try {
      const ldap = input.includes('@') ? input.split('@')[0] : input.trim();
      if (!ldap.trim() || ldap.includes('(')) {
        setSearchResult(null);
        return;
      }
      const response: any = await axios.get(`${urls.CIP_DOMAIN}/api/customer/v1/search`, {
        params: {
          input: ldap,
          conversationId: initialContactId,
          autolink,
          getSingleMatch
        },
        headers: getAuthHeaders(),
      });
      if (response.data?.success && response.data?.data?.customerDetails) {
        setSearchResult(response.data.data.customerDetails);
        if (response.data?.data?.customerDetails?.length > 0) {
          const customerData = response.data.data.customerDetails[0];
          if (!updateFields) {
            return;
          }
          const searchedResultLDAP = sessionStorage.getItem('searchedResultLDAP') ? JSON.parse(sessionStorage.getItem('searchedResultLDAP')) : {};
          sessionStorage.setItem('searchedResultLDAP', JSON.stringify({ ...searchedResultLDAP, [currentConvId]: customerData.ldap }));
          dispatch(updateSearchCustomerDetails({ linkedCustomer: customerData }));
          if (autolink) {
            dispatch(updateCustomerDetails({ linkedCustomer: customerData }));
            const eventData = {
              "firstName": customerData.firstName || '',
              "lastName": customerData.lastName || '',
              "email": customerData.email || '',
              "phone": customerData.originalPhone || '',
              "rengaId": customerData.ldap || '',
              "username": customerData.ldap || '',
              "contactId": currentConvId || '',
              "isDirectorOrAbove": customerData.isDirectorOrAbove || 'false'
            }
            callBackEvent('UPDATE_CUSTOMER_INFO', eventData)
          }
        }
      } else {
        setSearchResult('No employee found');
      }
    } catch (err) {
      console.error('Error searching employee:', err);
      setError('Error searching employee');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (value: string) => {
    setAgentInput(value);
    setInputHistory((prev) => ({ ...prev, [currentConvId]: value }));
    if (value.trim() === '') {
      setSearchResult(null);
    }
  };

  const handleKeyPress = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      const trimmedInput = agentInput.trim();
      setAgentInput(trimmedInput);
      setInputHistory((prev) => ({ ...prev, [currentConvId]: trimmedInput }));
      searchEmployee(trimmedInput, false, false);
    }
  };

  const handleEmployeeSelect = (ldap: string) => {
    searchEmployee(ldap, false, true, true);
  };

  const [debouncedAgentInput] = useDebounce(agentInput, 300);

  useEffect(() => {
    if (debouncedAgentInput && isConversationActive) {
      searchEmployee(debouncedAgentInput, false);
    }
  }, [debouncedAgentInput, isConversationActive]);

  const linkCustomer = (pressed) => {
    console.log('linkCustomerPressed', pressed);
    if (pressed && searchedUser?.linkedCustomer?.email && searchedUser?.linkedCustomer?.email !== userPersisted?.linkedCustomer?.email) {
      const { linkedCustomer: { ldap = '' } } = searchedUser;
      searchEmployee(ldap, true, true, true);
    }
  }

  useEffect(() => {
    if (searchedUser?.linkedCustomer?.email === userPersisted?.linkedCustomer?.email) {
      updateCustomerLink(true)
    } else {
      updateCustomerLink(false)
    }
  }, [searchedUser, userPersisted]);

  useEffect(() => {
    adjustAccordionHeight();
  }, [content?.current?.scrollHeight]);

  useEffect(() => {
    if (title === 'Employee Info') adjustAccordionHeight();
  }, [cipValues, searchResult]);

  const {
    darkMode,
    darkModeBG,
    darkModeBorder,
    darkModeTitle,
    darkModeFieldValues,
    darkModea
  } = themes(useSelector((state: any) => state.customerInfo.darkMode));

  const toggleAccordion = () => {
    const newActiveState = !active;
    setActive(newActiveState);
    setHeight(newActiveState ? `${content.current.scrollHeight}px` : '0px');
    setRotate(newActiveState ? 'accordion-icon rotate' : 'accordion-icon');
  };

  const adjustAccordionHeight = () => {
    setActive(true);
    setHeight(`${content.current.scrollHeight}px`);
    setRotate('accordion-icon rotate');
  };

  const language = useSelector((state: any) => state.customerInfo.language);

  const shouldDisplayFields = (email: string | string[]) => {
    if (Array.isArray(email)) {
      return email.length > 0;
    }
    return email !== '';
  };

  return (
    <div className={styles['accordion-section'] + darkModeBorder}>
      <button className={`${styles['accordion']} ${styles[active ? 'active' : '']} ${darkModeBG}`} onClick={toggleAccordion}>
        <Chevron className={rotate} width={17.93} fill={'#6E6E6E'} />
        <p className={styles['accordion-title'] + darkModeTitle}>{localization[language][title]}</p>
      </button>
      <div id={'accordion-content'} ref={content} style={{ maxHeight: `${height}`, paddingBottom: '5px' }} className={styles['accordion-content'] + darkModeBG}>
        {title === 'Contextual Info' && <CartDetails />}
        {title === 'Employee Info' ? (
          <div>
            <SearchAutocomplete
              width='100%'
              label="Search Employee (Name/LDAP/EmpID)"
              items={searchResult || []}
              inputValue={agentInput}
              onInputChange={handleInputChange}
              loadingState={loading ? 'loading' : 'idle'}
              onKeyDown={handleKeyPress}
              onSubmit={(_key, value) => {
                const selectedEmployee: any = searchResult?.find((item: any) => item.ldap === value);
                if (selectedEmployee && selectedEmployee.ldap) {
                  handleEmployeeSelect(selectedEmployee.ldap);
                } else {
                  setError('Employee not found');
                }
              }}
              UNSAFE_style={{ marginBottom: '10px' }}
              isDisabled={!isConversationActive}
            >
              {(item: Employee) => <Item key={item.ldap}>{`${item.ldap} (${item.name})`}</Item>}
            </SearchAutocomplete>
            {cipValues && shouldDisplayFields(cipValues['Email']) && (
              <div>
                {<div className={styles['flag-container']}>
                  {cipValues.sensitivity === '1' && <Checkbox isSelected={true}>Sensitivity</Checkbox>}
                  {cipValues.vip === '1' && <Vip isDark={darkMode} />}
                </div>}
                {Object.entries(cipValues).map(([key, value]) => (
                  !['Title', 'vip', 'sensitivity', 'isDirectorOrAbove'].includes(key) && value[0] && (
                    <div key={key} className={styles['accordion-content-fields']}>
                      {key === 'Full Name' ? (
                        <div className={styles['full-name-label-container']}>
                          <span className={`${styles['accordion-content-fields-keys-cip']}`}>
                            {key}
                          </span>
                          {cipValues.isDirectorOrAbove === 'true' && (
                            <span className={styles['director-star']} title="Director and Above">★</span>
                          )}
                        </div>
                      ) : (
                        <span className={`${styles['accordion-content-fields-keys-cip']}`}>
                          {key}
                        </span>
                      )}
                      {
                        key === 'Contact' && value[0] !== 'N/A' ? (
                          <button
                            onClick={() => callBackEvent('CIP_PHONE_NUMBER_CLICK', value[0])}
                            key={key}
                            className={styles['accordion-content-fields-values-phone'] + darkModeBG + darkModea}>
                            {value}
                          </button>
                        ) : key === 'Full Name' ? <div className={styles['fullname-container']}>
                          <span key={key} className={styles['accordion-content-fields-values-isnota'] + darkModeBG + darkModeFieldValues}>
                            {value}
                          </span>
                          {isConversationActive && <LinkedActionButton isDarkMode={darkMode} isAlreadyLinked={linked} linkEvent={linkCustomer} />}
                        </div> :
                          <span key={key} className={styles['accordion-content-fields-values-isnota'] + darkModeBG + darkModeFieldValues}>
                            {value}
                          </span>
                      }

                    </div>
                  )
                ))}
              </div>
            )}
          </div>
        ) : (
          Object.entries(cipValues).map(([key, values]: any) => {
            const hasRenderableValue =
              Array.isArray(values) &&
              values.length > 0 &&
              (typeof values[0] === 'object'
                ? Boolean(values[0]?.display)
                : Boolean(values[0]));

            return key !== 'Title' && hasRenderableValue ? (
              <div key={key} className={styles['accordion-content-fields']}>
                <span
                  className={`${styles[isCIP ? 'accordion-content-fields-keys-cip' : 'accordion-content-fields-keys-scip']} ${styles[key === 'CustomerOne Sub-Issue' ? (isCIP ? 'accordion-content-fields-keys-cip-sub-issue' : 'accordion-content-fields-keys-scip-sub-issue') : '']
                    }`}>
                  {localization[language][key] ?? key}
                </span>
                {values.map((value: any, index: number) => {
                  const isCaseLink =
                    key === 'Case Number' &&
                    value &&
                    typeof value === 'object' &&
                    value.display &&
                    value.url;

                  if (isCaseLink) {
                    return (
                      <a
                        key={index}
                        href={value.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={styles['accordion-content-fields-values-phone'] + darkModeBG + darkModea}
                      >
                        {value.display}
                      </a>
                    );
                  }

                  const displayValue =
                    typeof value === 'object' && value?.display ? value.display : value;

                  return (
                    <span key={index} className={styles['accordion-content-fields-values-isnota'] + darkModeBG + darkModeFieldValues}>
                      {localization[language][displayValue] ? localization[language][displayValue] : displayValue}
                    </span>
                  );
                })}
              </div>
            ) : null;
          })
        )}
      </div>
    </div>
  );
}

export default React.memo(Accordion, (prevProps, nextProps) => isEqual(prevProps.cipValues, nextProps.cipValues));